import { useCallback, useEffect, useRef, type Dispatch } from "react";
import { CallHubClient, type CallEvent } from "../../services/hub";
import { fetchMediaJoin, getSession } from "../../services/publicSession";
import { isSessionGone, needsLiveSync, pollIntervalMs, type PublicFlowAction, type PublicScreen } from "./publicFlow";

// While a public participant is waiting / starting / in the call: keep one SignalR connection
// (callAccepted / callEnded), poll the session as a presence heartbeat + missed-event backstop,
// and fetch the media join when the server reports the call started. The reducer accepts only
// the first join, so the hub event and the REST backstop never produce two calls.
export function usePublicLiveSync(args: {
  token: string | null;
  screen: PublicScreen;
  dispatch: Dispatch<PublicFlowAction>;
  onSessionGone: () => void;
}) {
  const { token, screen, dispatch, onSessionGone } = args;
  const hubRef = useRef<CallHubClient | null>(null);
  const selfIdRef = useRef("");
  const live = needsLiveSync(screen);
  const callId = screen.kind === "incall" ? screen.join.callId : null;

  // Hub connection for the whole live phase (not re-created between waiting → in call).
  useEffect(() => {
    if (!live || !token) return;
    let disposed = false;
    const onEvent = (ev: CallEvent) => {
      const id = Number(ev.data["callId"] ?? 0);
      if (ev.event === "callAccepted" && selfIdRef.current) {
        dispatch({ type: "joined", join: { callId: id, peerId: selfIdRef.current, mediaToken: String(ev.data["mediaToken"] ?? "") } });
      } else if (ev.event === "callEnded") {
        dispatch({ type: "callEnded" });
      }
    };
    const hub = new CallHubClient(onEvent, () => undefined);
    hubRef.current = hub;
    (async () => {
      try {
        await hub.connect(token);
        const me = await hub.register();
        if (!disposed) selfIdRef.current = String(me.userId);
      } catch {
        // The REST poll below still drives start/end; the hub is the fast path only.
      }
    })();
    return () => {
      disposed = true;
      hubRef.current = null;
      void hub.stop();
    };
  }, [live, token, dispatch]);

  // Join the call group once in the call so call-scoped events (callEnded) arrive.
  useEffect(() => {
    if (callId == null) return;
    hubRef.current?.setCurrentCall(callId);
    void hubRef.current?.joinCall(callId).catch(() => undefined);
  }, [callId]);

  // Heartbeat poll (also detects start / end / cancel / expiry if a hub event was missed).
  const interval = pollIntervalMs(screen);
  useEffect(() => {
    if (!token || interval == null) return;
    let stopped = false;
    const tick = () => {
      getSession(token)
        .then((view) => { if (!stopped) dispatch({ type: "view", view }); })
        .catch((e: unknown) => { if (!stopped && isSessionGone(e)) onSessionGone(); });
    };
    const id = window.setInterval(tick, interval);
    return () => { stopped = true; window.clearInterval(id); };
  }, [token, interval, dispatch, onSessionGone]);

  // Started on the server but no media join yet (missed event, refresh, reconnect).
  const starting = screen.kind === "starting";
  useEffect(() => {
    if (!starting || !token) return;
    let cancelled = false;
    const attempt = () => {
      fetchMediaJoin(token)
        .then((join) => { if (!cancelled) dispatch({ type: "joined", join }); })
        .catch((e: unknown) => { if (!cancelled && isSessionGone(e)) onSessionGone(); });
    };
    attempt();
    const id = window.setInterval(attempt, 3_000); // transient failure → retry until joined
    return () => { cancelled = true; window.clearInterval(id); };
  }, [starting, token, dispatch, onSessionGone]);

  /** End the running call through the hub (participant-checked server-side). */
  const endCall = useCallback(async (id: number): Promise<boolean> => {
    try {
      const ack = await hubRef.current?.endCall(id);
      return !!ack?.ok;
    } catch {
      return false;
    }
  }, []);

  return { endCall };
}
